import { cn } from "@/lib/utils";
import { EditorContent, EditorContext } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { Dots } from "../Indicator/Dots";
import { Scrollable } from "../Scrollable/Scrollable";
import { Row } from "../ui/flex";
import { BasicBubbleToolbar } from "./BasicBubbleToolbar";
import { BasicToolbar } from "./BasicToolbar";
import { useBasicEditor } from "./use-basic-editor";

export const BasicWriter: React.FC<{
  content?: string;
  onUpdate?: (html: string) => void;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  hasToolbar?: boolean;
  deps?: React.DependencyList;
  includeSkillMentions?: boolean;
  excludeSkillUuid?: string;
}> = ({
  content = "",
  onUpdate,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  hasToolbar = true,
  deps = [],
  includeSkillMentions = false,
  excludeSkillUuid,
}) => {
  const { editor, isWriting } = useBasicEditor({
    content,
    placeholder,
    onUpdate,
    onChange,
    writingDebounceMs: 1000,
    includeSkillMentions,
    excludeSkillUuid,
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (editor) {
      if (isFirstRender.current) {
        editor.commands.setContent(content);
        isFirstRender.current = false;
      }
    }
  }, [editor, content]);

  useEffect(() => {
    if (editor && !isFirstRender.current) {
      const currentContent = editor.getHTML().trim();
      const newContent = content.trim();

      if (currentContent !== newContent) {
        editor.commands.setContent(content);
      }
    }
  }, deps);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={cn(
        { "pt-16": hasToolbar },
        "relative flex min-h-[16rem] flex-grow flex-col overflow-hidden",
      )}
    >
      <EditorContext.Provider value={{ editor }}>
        {hasToolbar && (
          <BasicToolbar className="absolute top-0 right-0 left-0 z-10 bg-white" />
        )}
        <Scrollable outerClassName="grow">
          {/*
            The Bubble Menu needs to be a sibling to the Editor Content.
            This follows the same pattern as WriterContent.tsx to avoid DOM issues.
          */}
          <BasicBubbleToolbar />
          <EditorContent
            editor={editor}
            className="prose prose-sm markdown max-w-none focus:outline-none"
          />
        </Scrollable>

        {isWriting && (
          <Row
            className="bg-background-4 text-foreground fixed bottom-2 left-1/2 -translate-x-1/2 transform gap-2 rounded-full px-2 py-1 text-xs"
            align="center"
          >
            Saving
            <Dots />
          </Row>
        )}
      </EditorContext.Provider>

      {disabled && (
        <div className="bg-background/50 pointer-events-none absolute inset-0" />
      )}
    </div>
  );
};
